/**
 * Config Service - CRUD operations for global app configuration
 * Manages mobile config parameters (heartbeat, battery/storage thresholds)
 */

import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
} from "firebase/firestore";
import { db, auth } from "@/config/firebase";
import type { MobileConfig, CloudConfig } from "@/types";

const MOBILE_CONFIG_DOC = "config/mobile";

// Default values for mobile config
const DEFAULT_MOBILE_CONFIG: Omit<MobileConfig, "updatedAt" | "updatedBy"> = {
  heartbeatIntervalSeconds: 30,
  survivalBatteryPercent: 10,
  storageThresholdMB: 200,
  // Offline once lastSeen is older than heartbeat + this grace, i.e. 60s by default.
  // See hooks/useConnectionStatus.ts for why the grace is 30s.
  offlineGraceSeconds: 30,
  missedCaptureToleranceSeconds: 60, // Tolerance before marking capture as missed
  latestAppVersionCode: 0, // Updated by deploy script
};

/**
 * Get mobile config from Firestore
 * Returns default values if document doesn't exist
 */
export async function getMobileConfig(): Promise<MobileConfig> {
  const docRef = doc(db, MOBILE_CONFIG_DOC);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    // Merge with defaults so missing fields get default values
    return { ...DEFAULT_MOBILE_CONFIG, ...docSnap.data() } as MobileConfig;
  }

  // Return defaults if no config exists
  return DEFAULT_MOBILE_CONFIG as MobileConfig;
}

/**
 * Update mobile config in Firestore
 * Only admins can write (enforced by security rules)
 */
export async function updateMobileConfig(
  config: Partial<Omit<MobileConfig, "updatedAt" | "updatedBy">>
): Promise<void> {
  const docRef = doc(db, MOBILE_CONFIG_DOC);
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("User must be authenticated to update config");
  }

  // Merge with existing config
  const existingConfig = await getMobileConfig();

  await setDoc(docRef, {
    ...existingConfig,
    ...config,
    updatedAt: serverTimestamp(),
    updatedBy: currentUser.uid,
  });
}

/**
 * Subscribe to mobile config changes in real-time
 */
export function subscribeToMobileConfig(
  callback: (config: MobileConfig) => void
): Unsubscribe {
  const docRef = doc(db, MOBILE_CONFIG_DOC);

  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      // Merge with defaults so missing fields get default values
      callback({ ...DEFAULT_MOBILE_CONFIG, ...docSnap.data() } as MobileConfig);
    } else {
      callback(DEFAULT_MOBILE_CONFIG as MobileConfig);
    }
  });
}

/**
 * Get default mobile config values
 */
export function getDefaultMobileConfig(): Omit<MobileConfig, "updatedAt" | "updatedBy"> {
  return { ...DEFAULT_MOBILE_CONFIG };
}

// ============================================================================
// Cloud Config (download limits, storage settings)
// ============================================================================

const CLOUD_CONFIG_DOC = "config/cloud";

// Default values for cloud config
const DEFAULT_CLOUD_CONFIG: Omit<CloudConfig, "updatedAt" | "updatedBy"> = {
  browserDownloadLimitMB: 500, // 500 MB default limit for browser downloads
};

/**
 * Get cloud config from Firestore
 * Returns default values if document doesn't exist
 */
export async function getCloudConfig(): Promise<CloudConfig> {
  const docRef = doc(db, CLOUD_CONFIG_DOC);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data() as CloudConfig;
  }

  // Return defaults if no config exists
  return DEFAULT_CLOUD_CONFIG as CloudConfig;
}

/**
 * Update cloud config in Firestore
 * Only admins can write (enforced by security rules)
 */
export async function updateCloudConfig(
  config: Partial<Omit<CloudConfig, "updatedAt" | "updatedBy">>
): Promise<void> {
  const docRef = doc(db, CLOUD_CONFIG_DOC);
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("User must be authenticated to update config");
  }

  // Merge with existing config
  const existingConfig = await getCloudConfig();

  await setDoc(docRef, {
    ...existingConfig,
    ...config,
    updatedAt: serverTimestamp(),
    updatedBy: currentUser.uid,
  });
}

/**
 * Subscribe to cloud config changes in real-time
 */
export function subscribeToCloudConfig(
  callback: (config: CloudConfig) => void
): Unsubscribe {
  const docRef = doc(db, CLOUD_CONFIG_DOC);

  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback(docSnap.data() as CloudConfig);
    } else {
      callback(DEFAULT_CLOUD_CONFIG as CloudConfig);
    }
  });
}

/**
 * Get default cloud config values
 */
export function getDefaultCloudConfig(): Omit<CloudConfig, "updatedAt" | "updatedBy"> {
  return { ...DEFAULT_CLOUD_CONFIG };
}
